'use client';

import { useMemo } from 'react';
import { cn } from '@/shared/utils/tw';
import { useToastStore } from '../model/toast-store';
import { ToastText } from './toast-text';
import { ToastInfo } from './toast-info';
import { ToastWarning } from './toast-warning';
import { ToastError } from './toast-error';
import { ToastSTT } from './toast-stt';
import { ToastTTS } from './toast-tts';
import type { Toast, ToastType } from '../types/toast-types';

// 타입별 컴포넌트 매핑
const TOAST_COMPONENTS: Record<ToastType, React.FC<{ toast: Toast }>> = {
  text: ToastText,
  info: ToastInfo,
  warning: ToastWarning,
  error: ToastError,
  stt: ToastSTT,
  tts: ToastTTS,
};

/**
 * ToastContainer
 * 토스트를 화면에 렌더링하는 컨테이너 컴포넌트
 * - top/bottom 위치별 분리 렌더링
 * - z-[200]으로 모든 UI 위에 표시
 */
export const ToastContainer = () => {
  const toasts = useToastStore((state) => state.toasts);

  // 위치별로 토스트 분리
  const topToasts = useMemo(
    () => toasts.filter((t) => t.position === 'top'),
    [toasts]
  );
  const bottomToasts = useMemo(
    () => toasts.filter((t) => t.position === 'bottom'),
    [toasts]
  );

  if (toasts.length === 0) return null;

  return (
    <>
      {/* 상단 토스트 컨테이너 */}
      {topToasts.length > 0 && (
        <div
          className={cn(
            'absolute left-0 right-0 z-[200]',
            'flex flex-col items-center gap-2',
            'pointer-events-none'
          )}
          style={{ top: '7.51cqh' }}
        >
          {topToasts.map((toast) => {
            const ToastComponent = TOAST_COMPONENTS[toast.type];
            return <ToastComponent key={toast.id} toast={toast} />;
          })}
        </div>
      )}

      {/* 하단 토스트 컨테이너 */}
      {bottomToasts.length > 0 && (
        <div
          className={cn(
            'absolute bottom-0 left-0 right-0 z-[200]',
            'flex flex-col items-center gap-2',
            'pointer-events-none'
          )}
          style={{ paddingBottom: '148px' }}
        >
          {bottomToasts.map((toast) => {
            const ToastComponent = TOAST_COMPONENTS[toast.type];
            return <ToastComponent key={toast.id} toast={toast} />;
          })}
        </div>
      )}
    </>
  );
};
